<template>
  <div class="question-screen-wrapper">

    <v-progress-linear v-bind:indeterminate="true" height="5" v-if="showLoading"></v-progress-linear>

    <v-alert error value="true" v-if="errorMessage">
      {{ errorMessage }}
    </v-alert>

    <v-card class="my-4" v-if="challenge">

      <v-card-media
        class="white--text overlay"
        :src= urlComputed
        height="200px"
      >
        <v-container fill-height fluid>
          <v-layout fill-height>
            <v-flex xs12 align-end flexbox class="headline-wrapper">
              <span class="headline">
                {{ challenge.name }}
              </span>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-media>

      <v-card-title v-if="!challenge.question">
        <div class="headline">
          You have not unlocked this challenge yet...
        </div>
      </v-card-title>

      <v-card-text v-if="challenge.question" class="pt-4 pr-4 pl-4">
        <div class="question-wrapper">
          <div v-if="goodAnswer !== true">
            <v-card-text v-if="challenge.allowedAttempts && this.remainingAttempts > 0">
              <v-alert v-if="challenge.allowedAttempts === 1" warning value="true" transition="scale-transition">
                Be attentive! You'll get ONLY 1 attempt to solve this challenge
              </v-alert>
              <v-alert v-else warning value="true" transition="scale-transition">
                You'll get {{challenge.allowedAttempts}} attempts to solve this challenge
                <br>Remaining Attempts: {{this.remainingAttempts}}
              </v-alert>
            </v-card-text>
          </div>
          <div v-if="questionOnlyText">
            <vue-markdown :source="challenge.question"></vue-markdown>
          </div>
          <div v-else>
            <vue-markdown :source="challenge.question.textBeforeCode"></vue-markdown>
            <pre v-highlightjs="challenge.question.codeSnippet">
              <code :class="challenge.question.codeLanguage"></code>
            </pre>
            <vue-markdown v-if="challenge.question.textAfterCode" :source="challenge.question.textAfterCode"></vue-markdown>
          </div>
        </div>

        <multiple-answer
          v-if="challenge.type === 'multiple-answers'"
          :possibleAnswers="challenge.answers"
          @updated="onAnswerUpdate"
          :chosenAnswer="chosenAnswer"
          :disabledAnswers="disableAnswers"
        ></multiple-answer>

        <one-choice-answer
          v-if="challenge.type === 'one-answer'"
          :possibleAnswers="challenge.answers"
          @updated="onAnswerUpdate"
          :chosenAnswer="chosenAnswer"
          :disabledAnswers="disableAnswers"
        ></one-choice-answer>

        <text-answer
          v-if="challenge.type === 'free-text'"
          @updated="onAnswerUpdate"
          :chosenAnswer="chosenAnswer"
          :disabledAnswers="disableAnswers"
        ></text-answer>

      </v-card-text>

      <v-card-text>
        <v-alert success hide-icon v-if="goodAnswer === true" value="true" transition="scale-transition">

          <div v-if="score">
            <h5 class="text-xs-center">Bravo! <strong> {{ score }} {{score | pluralize('point')}} won!</strong></h5>
            <v-container grid-list-md>
              <v-layout row wrap>
                <ul class="bonuses">
                  <li v-if="showQuestionPoints">
                    <v-icon class="amber--text text--lighten-2">done</v-icon>
                    Challenge solved: <strong>{{ questionPoints }} {{questionPoints | pluralize('point')}}</strong>
                  </li>
                  <li v-if="showBonusFirstTry">
                    <v-icon class="amber--text text--lighten-2">done</v-icon>
                    First try bonus: <strong>+{{ bonusFirstTry }} {{bonusFirstTry | pluralize('point')}}</strong>
                  </li>
                  <li v-if="showBonusSpeed">
                    <v-icon class="amber--text text--lighten-2">done</v-icon>
                    Speed bonus: <strong>+{{ bonusSpeed }} {{bonusSpeed | pluralize('point')}}</strong>
                  </li>
                </ul>
              </v-layout>
            </v-container>
            <picture-input
              v-if="!photoUploaded"
              ref="pictureInput"
              @change="onPictureChosen"
              width="600"
              height="600"
              margin="16"
              accept="image/jpeg,image/png"
              size="10"
              :customStrings="{
                upload: '<h1>Bummer!</h1>',
                drag: 'Choose picture'
              }">
            </picture-input>
            <v-btn primary @click.native="startPictureUpload()" :disabled="showLoading" v-if="photoToUpload&&!photoUploaded" style="width:100%;">
              <span v-if="showLoading">Uploading...</span>
              <span v-else>Upload</span>

            </v-btn>
          </div>
          <div v-else>
            <h5 class="text-xs-center"><strong>Challenge Solved!</strong></h5>
          </div>

        </v-alert>
      </v-card-text>

      <v-card-text>
        <v-alert warning v-if="goodAnswer === false && this.remainingAttempts !==0" value="true" transition="scale-transition">
          :( Too bad, wrong one.
        </v-alert>
        <v-alert v-if="this.remainingAttempts === 0" warning value="true" transition="scale-transition">
          Sorry! You can't answer this challenge as you have no more chances left  :(
        </v-alert>
      </v-card-text>

      <v-card-text v-if="errorOnCheckAnswer">
        <v-alert error value="true" transition="scale-transition">
          An error occurred. We were unable to check your answer... :(
        </v-alert>
      </v-card-text>

      <v-card-text v-if="photoUploaded">
        <v-alert success value="true" transition="scale-transition">
          Successfully uploaded your picture!
        </v-alert>
      </v-card-text>

      <v-card-actions class="ml-2 mt-3">
        <v-btn flat to="/Home">
          <v-icon>fa-chevron-left</v-icon>
          Back to the maps
        </v-btn>
        <v-btn primary @click.native="submitAnswer()" v-if="isSubmittable" :loading="goButtonLoading">
          Go!
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-dialog v-model="dialog">
      <v-card>
        <v-card-title>
          <div class="headline">
            Good answer! Bravo!<br>
            To save your progress, please login
          </div>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="green--text darken-1" flat="flat" @click.native="dialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
  import _ from 'lodash'
  import { mapGetters, mapActions } from 'vuex'
  import VueMarkdown from 'vue-markdown'
  import TeamLogin from '@/components/TeamLogin'
  import MultipleAnswer from '@/components/single/MultipleAnswer'
  import TextAnswer from '@/components/single/TextAnswer'
  import OneChoiceAnswer from '@/components/single/OneChoiceAnswer'
  import {challengeStatus} from '@/services/challengeStatus.enum'
  import PictureInput from 'vue-picture-input'

  const getters = mapGetters([
    'challengesLocations',
    'team',
  ])

  const actions = mapActions([
    'getChallengesLocations',
    'sendAnswer',
    'uploadPicture',
  ])

  export default {
    name: 'question',
    components: {
      TeamLogin,
      MultipleAnswer,
      TextAnswer,
      VueMarkdown,
      OneChoiceAnswer,
      PictureInput,
    },
    data() {
      return {
        photoToUpload: null,
        photoUploaded: false,
        showLoading: false,
        answer: null,
        show: false,
        chosenAnswer: null,
        goodAnswer: null,
        disableAnswers: false,
        goButtonLoading: false,
        errorMessage: '',
        errorOnCheckAnswer: false,
        dialog: false,

        questionPoints: null,
        bonusFirstTry: null,
        bonusSpeed: null,
        score: null,

        showQuestionPoints: false,
        showBonusFirstTry: false,
        showBonusSpeed: false,
        showScore: false,
        challengeStatus: null,
        remainingAttempts: -1,
      }
    },
    created() {
      if (!this.challengesLocations || this.challengesLocations.length === 0) {
        // Reload from API
        this.showLoading = true
        this.getChallengesLocations()
          .then(() => {
            if (this.challenge && this.challenge.remainingAttempts !== null) {
              this.remainingAttempts = this.challenge.remainingAttempts
              if (this.remainingAttempts === 0) {
                this.disableAnswers = true
              }
            }
            this.showLoading = false
          })
      }

      if (this.challenge && this.challenge.status === challengeStatus.SOLVED) {
        this.updateScreenToAnsweredState()
      }

      if ((this.challenge && this.challenge.status === challengeStatus.FAILED) || this.remainingAttempts === 0) {
        this.updateScreenToFailedState()
      }

    },
    mounted() {
      // Go back to 'Map' page when pressing 'Esc'
      if (this.challenge && this.challenge.remainingAttempts !== null) {
        this.remainingAttempts = this.challenge.remainingAttempts
        if (this.remainingAttempts === 0) {
          this.disableAnswers = true
        }
      }
      const escKeyCode = 27
      window.addEventListener('keyup', event => {
        if (event.keyCode === escKeyCode) {
          this.$router.push({ name: 'Home' })
        }
      })
    },
    computed: {
      ...getters,
      urlComputed() {
        return process.env.BUCKET_PREFIX + this.challenge.imgSrc
      },

      /**
       * We could have put that in the .then() of mounted() / this.getChallengesLocations()
       * Or here as a computed property as it will get re-evaluated when 'this.challengesLocations' changes
       */
      challenge() {
        const challengeToDisplay = _.find(this.challengesLocations, question => {
          return question.challengeId === this.$route.params.id
        })
        if (this.challengesLocations && this.challengesLocations.length > 0 && !challengeToDisplay) {
          this.errorMessage = 'Oops, invalid challenge ID...'
        } else {
          this.errorMessage = ''
        }
        return challengeToDisplay
      },
      questionOnlyText() {
        return typeof this.challenge.question === 'string'
      },
      isSubmittable() {
        return this.challenge.question && !this.disableAnswers && this.challenge.status === challengeStatus.UNLOCKED
      }
    },
    methods: {
      ...actions,
      onPictureChosen(photo) {
        console.log('New picture selected!')
        if (photo) {
          this.photoToUpload = photo
        } else {
          console.log('FileReader API not supported: use the <form>, Luke!')
        }
      },
      startPictureUpload() {
        const challengeId = this.$route.params.id
        this.showLoading = true
        this.uploadPicture({
          challengeId, photo: this.photoToUpload
        })
          .then(response => {
            console.log('Success uploading photo!' + response)
            this.photoUploaded = true
            this.showLoading = false
            this.errorMessage = ''
          }).catch(err => {
            console.log('Error uploading photo!' + err)
            this.showLoading = false
            this.errorMessage = err.message
          })
      },
      submitAnswer() {

        this.goodAnswer = null
        this.goButtonLoading = true
        this.errorOnCheckAnswer = false

        const challengeId = this.$route.params.id

        this.sendAnswer({
          challengeId,
          answer: this.answer + '',
        })
          .then(response => {
            const responseData = response.data

            if (responseData.error) {
              this.errorOnCheckAnswer = true
              console.error('Error back from server:', responseData.error)
            } else if (responseData.code) {
              this.errorOnCheckAnswer = true
              console.error('Error back from server:', responseData.code, responseData.message)
            } else if (responseData.isValid) {
              const scoreObj = responseData.score
              this.score = scoreObj.score
              this.questionPoints = scoreObj.questionPoints
              this.bonusFirstTry = scoreObj.bonusFirstTry
              this.bonusSpeed = scoreObj.bonusSpeed
              this.onGoodAnswer()
            } else {
              this.goodAnswer = false
              this.status = responseData.status
              this.remainingAttempts = responseData.remainingAttempts
              this.$store.commit('UPDATE_CHALLENGE_REMAINING_ATTEMPTS', {
                challengeId,
                remainingAttempts: this.remainingAttempts,
              })
              if (responseData.remainingAttempts === 0) {
                this.disableAnswers = true
                this.$store.commit('MARK_CHALLENGE_AS_FAILED', {
                  challengeId,
                  challengeAnswer: this.answer,
                })
              }
            }
            this.goButtonLoading = false
          })
          .catch(err => {
            console.error(err)
            this.errorOnCheckAnswer = true
            this.goButtonLoading = false
          })
      },
      updateScreenToAnsweredState() {
        this.chosenAnswer = this.challenge.chosenAnswer
        this.disableAnswers = true
        this.goodAnswer = true
      },
      updateScreenToFailedState() {
        this.disableAnswers = true
      },
      onAnswerUpdate(answer) {
        this.answer = answer
      },
      onGoodAnswer() {
        this.challengeDone()
      },
      loginSuccess() {
        this.dialog = false
        // Points have already been transferred from the unlogged state
        const addPoints = false
        this.challengeDone(addPoints)
      },
      challengeDone(addPoints = true) {

        const challengeId = this.$route.params.id

        this.showScoreDetails()

        this.$store.commit('MARK_CHALLENGE_AS_DONE', {
          challengeId,
          challengeAnswer: this.answer,
        })
        if (addPoints) {
          this.$store.commit('ADD_POINTS_TO_USER_SCORE', this.score)
        }
        this.updateScreenToAnsweredState()
      },
      showScoreDetails() {
        this.goodAnswer = true
        let currentTimer = 0
        const delay = 500
        currentTimer += delay
        setTimeout(() => {
          this.showQuestionPoints = true
        }, currentTimer)
        if (this.bonusFirstTry) {
          currentTimer += delay
          setTimeout(() => {
            this.showBonusFirstTry = true
          }, currentTimer)
        }
        if (this.bonusSpeed) {
          currentTimer += delay
          setTimeout(() => {
            this.showBonusSpeed = true
          }, currentTimer)
        }
        currentTimer += delay
        setTimeout(() => {
          this.showScore = true
        }, currentTimer)
      },
    },
  }
</script>

<style lang="scss" type="text/scss" scoped>

  .question-screen-wrapper {
    background-color: #fafafa;
  }

  .card {
    max-width: 500px;
    padding-bottom: 20px;

    /deep/ {

      .card__media {
        .headline-wrapper {
          z-index: 1;
          align-self: flex-end;
          text-align: left;

          .headline {
            text-shadow: 1px 2px #000000;
          }
        }
      }

      .card__text {
        padding-top: 0;
        padding-bottom: 0;

        .question-wrapper {
          text-align: left;

          p {
            font-size: 18px;
          }

          code {
            width: 100%;
            overflow-wrap: break-word;
            word-wrap: break-word;
            -ms-word-break: break-all;
            word-break: break-word;
          }
        }

        .alert {
          margin-top: 25px;

          h5 {
            margin-bottom: 8px;
          }

          > div {
            text-align: left;
            padding-top: 1px;
          }
        }

        .input-group--text-field {
          margin: 0 0 18px 0;
        }
      }

      .input-group__details {
        min-height: 2px;
      }

      .card__actions .fa-chevron-left {
        font-size: 1.2rem;
        margin-right: 10px;
      }
    }
  }

  .card {
    margin: auto;
  }
  @media screen and (max-width: 548px) {
    .card {
      margin: 24px;
    }
  }

  .overlay {
    position: relative;
  }

  .overlay:after {
    position: absolute;
    content:"";
    top:0;
    left:0;
    width:100%;
    height:100%;
    background-color: rgba(0, 0, 0, 0.3);
  }

  .bonuses {
    list-style: none;
    padding: 0;
  }

  .bonuses li {
    opacity: 0;
    animation: fadeIn 0.9s 1;
    animation-fill-mode: forwards;
  }

  .score {
    font-size: 30px;
    opacity: 0;
    animation: fadeIn 0.9s 1;
    animation-fill-mode: forwards;
  }

  @keyframes fadeIn {
    0% {
      opacity: 0.0;
    }
    100% {
      opacity: 1.0;
    }
  }

  @media screen and (max-width: 548px) {
    .dialog .card {
      margin: 0;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/Question.vue